import React, { useState } from 'react';
import { observable, flow } from 'mobx';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import dayjs from 'dayjs';

import { 
    Colours, 
    Sizes, 
    Paragraph, 
    isAddressComplete, 
    FormattedStreetAddress, 
    DOCUMENT_ID_QUERY_PARAM_KEY,
    noticeError,
} from '@ratehub/base-ui';
import { INS_DOCUMENT_EXPIRY_DATE_LIMIT } from '@ratehub/documents/ins-common/stable';

import AnimatedDeleteContainer from './AnimatedDeleteContainer';
import SavedDocumentsRowInsurance from './SavedDocumentsRowInsurance';
import QuoteSummary from './QuoteSummary';
import QuoteDetailsHome from './QuoteDetailsHome';


function SavedDocumentsRowHome({ model, document, onRemoveRow }) {
    const intl = useIntl();
    const [ homeModel ] = useState(
        model && document
            ? () => createHomeModel(model, document)
            : null,
    );

    return (
        <Container
            isDeleted={homeModel?.isDeletedFromBackend}
            isCompleted={homeModel?.isCompleted}
        >
            <Choose>
                <When condition={!!homeModel}>
                    <AnimatedDeleteContainer
                        className="document"
                        canDelete={!homeModel.isCompleted}
                        shouldShowSpinner={homeModel.isDeleting}
                        onDelete={homeModel.deleteDocument}
                        isItemDeleted={homeModel.isDeletedFromBackend}
                        onDeleteComplete={onRemoveRow}
                        showErrorMessage={homeModel.hasFailedDeletion}
                        confirmationTitle={Messages.DELETE_TITLE}
                        confirmText={Messages.CONFIRM_DELETE}
                        rejectText={Messages.REJECT_DELETE}
                        successTitle={intl.formatMessage(Messages.SUCCESSFUL_DELETE)}
                        errorTitle={intl.formatMessage(Messages.ERROR_DELETE)}
                    >
                        <SavedDocumentsRowInsurance
                            pillMessage={!homeModel.isCompleted
                                && (
                                    <FormattedMessage
                                        id="Dashboard.QuoteRowHome.expiresInDays"
                                        defaultMessage="Expires in <strong>{amount} days</strong>"
                                        values={{ amount: homeModel.daysLeft }}
                                    />
                                )
                            }
                            href={homeModel.isCompleted ? undefined : homeModel.resumeHREF}
                            hrefMessage={Messages.RESUME_HREF}
                            SummaryComponent={(
                                <QuoteSummary
                                    className="quote-summary"
                                    title={(
                                        <If condition={isAddressComplete(homeModel.address)}>
                                            <FormattedStreetAddress
                                                address={homeModel.address}
                                                shouldIncludeCity={true}
                                            />
                                        </If>
                                    )}
                                    isCompleted={homeModel.isCompleted}
                                    monthlyRate={homeModel.monthlyRate}
                                    yearlyRate={homeModel.yearlyRate}
                                />
                            )}
                            details={homeModel.isCompleted
                                ? [
                                    { label: Messages.INSURANCE_PROVIDER_LABEL, value: homeModel.insurerName },
                                    { label: Messages.QUOTE_COMPLETED_LABEL, value: homeModel.dateLastSaved },
                                ]
                                : [
                                    { label: Messages.QUOTE_STARTED_LABEL, value: homeModel.dateCreated },
                                ]}
                            data-name="Dashboard-resumeQuote"
                            isCompleted={!!homeModel.isCompleted}
                        >
                            <If condition={homeModel.isCompleted}>
                                <QuoteDetailsHome
                                    coverage={homeModel.coverage}
                                    endorsements={homeModel.endorsements}
                                    brokerName={homeModel.brokerName}
                                    brokerPhone={homeModel.brokerPhone}
                                />
                            </If>
                        </SavedDocumentsRowInsurance>
                    </AnimatedDeleteContainer>
                </When>
                <Otherwise>
                    <Paragraph
                        className="no-document"
                        size="medium"
                        message={Messages.NO_QUOTES}
                    />
                </Otherwise>
            </Choose>
        </Container>
    );
}

SavedDocumentsRowHome.propTypes = {
    model: PropTypes.object,
    document: PropTypes.object,
    onRemoveRow: PropTypes.func,
};

SavedDocumentsRowHome.defaultProps = {
    model: undefined,
    document: undefined,
    onRemoveRow: undefined,
};


// Example: /insurance/home-quotes?documentID=
const RESUME_URL_EN = `/insurance/home-quotes?${DOCUMENT_ID_QUERY_PARAM_KEY}=`;

function createHomeModel(model, document) {
    const self = observable({
        model,
        document,

        isDeleting: false,
        isDeletedFromBackend: false,
        hasFailedDeletion: false,

        get quote() {
            return self.document.quote;
        },
        get application() {
            return self.document.application;
        },
        get providerStore() {
            return self.model.context.insuranceProviderStore;
        },
        get insurer(){
            return self.providerStore.getInsurerById(self.quote?.insurerId);
        },
        get insurerName() {
            return self.insurer?.name ?? null;
        },
        get broker() {
            return self.providerStore.getBrokerById(self.quote?.brokerId);
        },
        // Broker can be null if quote is direct, we default values to insurer if this is the case
        get brokerName() {
            return (self.broker ? self.broker?.name : self.insurer?.name) ?? null;
        },
        get brokerPhone() {
            return (self.broker ? self.broker?.phoneNumber : self.insurer?.phoneNumber) ?? null;
        },
        get address() {
            return self.application?.property?.address;
        },
        get isCompleted() {
            return self.quote && self.quote.id !== null;
        },
        get dateCreated() {
            return dayjs(self.document.createdAt).format('MMM D, YYYY');
        },
        get dateLastSaved() {
            // very old document might not have updatedAt, they only have dateCreated
            // However, migration to v8 will change them turn "dateCreated" to "createdAt"
            return dayjs(self.document.userUpdatedAt ?? self.document.updatedAt ?? self.document.createdAt).format('MMM D, YYYY');
        },
        get daysLeft() {
            return self.isCompleted
                ? null
                : dayjs(self.document.userUpdatedAt ?? self.document.updatedAt ?? self.document.createdAt)
                    .clone()
                    .add(INS_DOCUMENT_EXPIRY_DATE_LIMIT, 'days')
                    .diff(dayjs(), 'days') + 1;
        },
        get monthlyRate() {
            return self.isCompleted
                ? self.quote.monthlyRate
                : null;
        },
        get yearlyRate() {
            return self.isCompleted
                ? self.quote.yearlyRate
                : null;
        },
        get coverage() {
            return self.isCompleted
                ? {
                    policyLength: self.quote.termInMonths,
                    personalLiability: self.application.coverage.personalLiability,
                    claimsDeductible: self.application.coverage.claimsDeductible,
                }
                : null;
        },
        get endorsements() {
            return self.isCompleted
                ? {
                    sewerBackup: self.application.coverage.sewerBackup,
                    overlandWater: self.application.coverage.overlandWater,
                }
                : null;
        },
        get resumeHREF() {
            // Example: /insurance/home-quotes?documentID=<document-id>
            return `${RESUME_URL_EN}${self.document.id}`;
        },

        deleteDocument: flow(function* (){
            if (self.isDeletedFromBackend){
                noticeError(new Error(`[SavedDocumentsRowHome] Attempted to delete document ${self.document.id}, which is already deleted`));
                return null;
            }

            self.isDeleting = true;
            self.hasFailedDeletion = false;

            try {
                yield self.model.context.sessionStore.deleteDocument(self.document.id);
                self.isDeletedFromBackend = true;
            } catch {
                // Error is noticed in sessionStore.deleteDocument
                self.isDeleting = false;
                self.hasFailedDeletion = true;
            }

            self.isDeleting = false;
        }),
    });

    return self;
}

const Container = styled.div`
    > .no-document {
        text-align: center;
        margin: ${Sizes.SPACING.ONE_AND_A_HALF};
        color: ${Colours.STONE_DARKEST};
    }
    .document {
        position: relative;
        ${props => !props.isDeleted && `
            margin: ${Sizes.SPACING.ONE} ${Sizes.SPACING.ONE_AND_A_HALF};
        `}
        ${props => props.isCompleted && `
            > .quote-summary {
                margin-bottom: 0;
            }
        `
}
`;

const Messages = defineMessages({
    NO_QUOTES: {
        id: 'Accounts.homeQuoteHistoryTable.noQuotes',
        defaultMessage: 'You have no home insurance quotes in progress.',
    },
    START_QUOTE: {
        id: 'Accounts.homeQuoteHistoryTable.startQuote',
        defaultMessage: 'Start my home quote',
    },
    TOOLTIP: {
        id: 'Accounts.homeQuoteHistoryTable.tooltip',
        defaultMessage: 'Find all your Home Insurance Quotes here',
    },
    DELETE_TITLE: {
        id: 'Accounts.homeQuoteHistoryTable.deleteConfirmationTitle',
        defaultMessage: 'Are you sure you want to delete this quote?',
    },
    CONFIRM_DELETE: {
        id: 'Accounts.homeQuoteHistoryTable.confirmDelete',
        defaultMessage: 'Yes, delete quote',
    },
    REJECT_DELETE: {
        id: 'Accounts.homeQuoteHistoryTable.cancelDelete',
        defaultMessage: 'Cancel, keep quote',
    },
    SUCCESSFUL_DELETE: {
        id: 'Accounts.homeQuoteHistoryTable.deleteSuccess',
        defaultMessage: 'Quote deleted successfully!',
    },
    ERROR_DELETE: {
        id: 'Accounts.homeQuoteHistoryTable.deleteFailure',
        defaultMessage: 'Unable to delete quote at this time. Please retry.',
    },
    RESUME_HREF: {
        id: 'Dashboard.QuoteHistoryTableHome.resumeQuote',
        defaultMessage: 'Resume quote',
    },
    INSURANCE_PROVIDER_LABEL: {
        id: 'Dashboard.QuoteHistoryTableHome.InsuranceProvider',
        defaultMessage: 'Insurance provider:',
    },
    QUOTE_COMPLETED_LABEL: {
        id: 'Dashboard.QuoteHistoryTableHome.QuoteCompleted',
        defaultMessage: 'Quote completed:',
    },
    QUOTE_STARTED_LABEL: {
        id: 'Dashboard.QuoteHistoryTableHome.QuoteStarted',
        defaultMessage: 'Quote started:',
    },
    EXPIRES_IN_DAYS: {
        id: 'Dashboard.QuoteHistoryTableHome.expiresInDays',
        defaultMessage: 'Expires in <strong>{amount} days</strong>',
    },
});

export default observer(SavedDocumentsRowHome);
